import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Button
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  ArrowBack as ArrowBackIcon,
  Assessment as AssessmentIcon,
  Warning as WarningIcon,
  TrendingUp as TrendingUpIcon,
  Insights as InsightsIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const ImpactAnalysisFeature: React.FC = () => {
  const navigate = useNavigate();

  const benefits = [
    'Understand downstream effects before implementing changes',
    'Reduce risk of unintended consequences',
    'Better planning and resource allocation',
    'Improved stakeholder communication',
    'Faster decision-making with confidence',
    'Minimized system disruptions and downtime'
  ];

  const capabilities = [
    {
      icon: <AssessmentIcon sx={{ color: 'primary.main' }} />,
      title: 'Dependency Analysis',
      description: 'Identify all systems, reports, and processes affected by proposed changes'
    },
    {
      icon: <WarningIcon sx={{ color: 'primary.main' }} />,
      title: 'Risk Assessment',
      description: 'Evaluate potential risks and impacts across the data ecosystem'
    },
    {
      icon: <TrendingUpIcon sx={{ color: 'primary.main' }} />,
      title: 'Scenario Modeling',
      description: 'Model different change scenarios to understand their implications'
    },
    {
      icon: <InsightsIcon sx={{ color: 'primary.main' }} />,
      title: 'Impact Reports',
      description: 'Generate comprehensive reports for stakeholders and decision-makers'
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(-1)}
        sx={{ mb: 3, color: 'primary.main' }}
      >
        Back
      </Button>

      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <AssignmentIcon sx={{ fontSize: 48, color: 'primary.main', mr: 2 }} />
          <Typography variant="h3" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
            Impact Analysis
          </Typography>
        </Box>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
          Assessment capabilities to understand downstream effects of proposed data changes
        </Typography>
        
        {/* Access Application Button */}
        <Button
          variant="contained"
          size="large"
          onClick={() => navigate('/analytics')}
          sx={{
            bgcolor: 'primary.main',
            color: 'white',
            px: 4,
            py: 1.5,
            fontSize: '1.1rem',
            fontWeight: 600,
            textTransform: 'none',
            '&:hover': {
              bgcolor: 'primary.dark'
            }
          }}
        >
          Access Analytics & Impact Analysis Application →
        </Button>
      </Box>

      <Paper sx={{ p: 4, mb: 4, borderLeft: '4px solid #003366' }}>
        <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
          Overview
        </Typography>
        <Typography paragraph>
          Impact Analysis provides comprehensive assessment capabilities to understand the downstream effects of proposed data changes before implementation. This proactive approach helps organizations make informed decisions, reduce risk, and minimize disruptions.
        </Typography>
        <Typography paragraph>
          By analyzing dependencies, data lineage, and system relationships, teams can identify all affected components and stakeholders, enabling better planning and communication throughout the change process.
        </Typography>
      </Paper>

      <Paper sx={{ p: 4, mb: 4, bgcolor: '#f8f9fa' }}>
        <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
          Key Benefits
        </Typography>
        <List>
          {benefits.map((benefit, index) => (
            <ListItem key={index}>
              <ListItemIcon>
                <CheckCircleIcon sx={{ color: 'primary.main' }} />
              </ListItemIcon>
              <ListItemText primary={benefit} />
            </ListItem>
          ))}
        </List>
      </Paper>

      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 600, mb: 3 }}>
        Core Capabilities
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {capabilities.map((capability, index) => (
          <Grid item xs={12} md={6} key={index}>
            <Card sx={{ height: '100%', borderLeft: '4px solid #003366' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  {capability.icon}
                  <Typography variant="h6" sx={{ ml: 1, color: 'primary.main', fontWeight: 600 }}>
                    {capability.title}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {capability.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Paper sx={{ p: 4, mb: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
          Common Use Cases
        </Typography>
        <Divider sx={{ mb: 3 }} />
        
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
            System Upgrades
          </Typography>
          <Typography paragraph>
            Assess the impact of system upgrades on downstream applications and data consumers.
          </Typography>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
            Schema Changes
          </Typography>
          <Typography paragraph>
            Understand which reports, dashboards, and applications will be affected by database schema changes.
          </Typography>
        </Box>

        <Box>
          <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
            Data Migration
          </Typography>
          <Typography paragraph>
            Plan data migrations with full visibility into affected systems and stakeholders.
          </Typography>
        </Box>
      </Paper>

      <Paper sx={{ p: 4, bgcolor: 'primary.main', color: 'white', textAlign: 'center' }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
          Ready to Assess Your Data Changes?
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          Learn more about how E-Unify's Impact Analysis can help your organization
        </Typography>
        <Button
          variant="contained"
          size="large"
          onClick={() => navigate('/contact')}
          sx={{
            bgcolor: 'white',
            color: 'primary.main',
            '&:hover': { bgcolor: '#f8f9fa' }
          }}
        >
          Contact Us
        </Button>
      </Paper>
    </Container>
  );
};

export default ImpactAnalysisFeature;
